import React, { useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { usePortfolio } from "../context/PortfolioContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { fmtINR, fmtUnits, longDate } from "../lib/format";
import { ArrowDownLeft, ArrowUpRight, Loader2, Pencil, Trash2, X, TrendingUp, TrendingDown, Scale } from "lucide-react";
import StatCard from "../components/StatCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { stampedName } from "../lib/csv";
import ExportButtons from "../components/ExportButtons";

export default function Cashflows() {
  const { user } = useAuth();
  const { transactions, members, loading, reload } = usePortfolio();
  const { toast } = useToast();
  const [filter, setFilter] = useState("all");
  const [memberId, setMemberId] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === "admin";

  const filtered = useMemo(() => {
    return transactions
      .filter((c) => c.type !== "bonus") // Bonus has its own dedicated page
      .filter((c) => {
        if (filter === "subs") return (c.fundsCr || 0) > 0;
        if (filter === "reds") return (c.fundsDr || 0) > 0;
        return true;
      })
      .filter((c) => memberId === "all" ? true : c.memberId === memberId)
      .filter((c) => {
        if (fromDate && c.date < fromDate) return false;
        if (toDate && c.date > toDate) return false;
        return true;
      })
      .slice()
      .sort((a, b) => {
        const da = new Date(a.date);
        const db = new Date(b.date);
        if (db - da !== 0) return db - da;
        return (b.id || "").localeCompare(a.id || "");
      });
  }, [transactions, filter, memberId, fromDate, toDate]);

  // Running balance (computed in chronological asc order, then mapped by id)
  // Only subscription/redemption are included — bonus is on a separate page
  const runningById = useMemo(() => {
    const asc = [...filtered].sort((a, b) => {
      const da = new Date(a.date);
      const db = new Date(b.date);
      if (da - db !== 0) return da - db;
      return (a.id || "").localeCompare(b.id || "");
    });
    let bal = 0;
    const map = {};
    for (const t of asc) {
      bal += (t.fundsCr || 0) - (t.fundsDr || 0);
      map[t.id] = bal;
    }
    return map;
  }, [filtered]);

  const exportData = useMemo(() => {
    const headers = ["Date", "Member", "Type", "NAV", "Units", "Amount", "AMC Fee", "Running Balance", "Details"];
    const rows = filtered.map((c) => {
      const inflow = (c.fundsCr || 0) > 0;
      const amount = inflow ? c.fundsCr : -c.fundsDr;
      const units = inflow ? c.unitsCr : c.unitsDr;
      return [
        c.date,
        c.name,
        inflow ? "Subscription" : "Redemption",
        c.nav,
        units,
        amount,
        c.amcFee || 0,
        runningById[c.id] || 0,
        c.details || "",
      ];
    });
    const pdfColumns = ["Date", "Member", "Type", "NAV", "Units", "Amount", "Running"];
    const pdfRows = filtered.map((c) => {
      const inflow = (c.fundsCr || 0) > 0;
      const amount = inflow ? c.fundsCr : -c.fundsDr;
      const units = inflow ? c.unitsCr : c.unitsDr;
      return [
        c.date,
        c.name,
        inflow ? "Subscription" : "Redemption",
        "₹" + c.nav.toFixed(4),
        fmtUnits(units),
        fmtINR(amount),
        fmtINR(runningById[c.id] || 0),
      ];
    });
    return { headers, rows, pdfColumns, pdfRows };
  }, [filtered, runningById]);

  if (loading)
    return (
      <AppShell>
        <div className="flex items-center py-24 text-slate-400">
          <Loader2 className="animate-spin mr-2" /> Loading…
        </div>
      </AppShell>
    );

  const totals = filtered.reduce(
    (acc, c) => {
      acc.in += c.fundsCr || 0;
      acc.out -= c.fundsDr || 0;
      return acc;
    },
    { in: 0, out: 0 }
  );

  const clearFilters = () => {
    setFilter("all");
    setMemberId("all");
    setFromDate("");
    setToDate("");
  };
  const filtersActive = filter !== "all" || memberId !== "all" || fromDate || toDate;

  const updateEdit = (k, v) => {
    setEditing((e) => {
      const next = { ...e, [k]: v };
      const funds = parseFloat(next.funds);
      const nav = parseFloat(next.nav);
      if (!isNaN(funds) && !isNaN(nav) && nav > 0 && (k === "funds" || k === "nav")) {
        next.units = (funds / nav).toFixed(4);
      }
      return next;
    });
  };

  const startEdit = (t) => {
    const isCredit = t.type === "subscription" || t.type === "bonus" || (t.fundsCr || 0) > 0;
    setEditing({
      id: t.id,
      memberName: t.name,
      date: t.date,
      type: t.type,
      details: t.details || "",
      funds: String(isCredit ? t.fundsCr : t.fundsDr),
      units: String(isCredit ? t.unitsCr : t.unitsDr),
      nav: t.nav.toFixed(4),
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const funds = parseFloat(editing.funds);
    const nav = parseFloat(editing.nav);
    const units = parseFloat(editing.units);
    if (isNaN(funds) || funds <= 0) return toast({ title: "Invalid amount" });
    if (isNaN(nav) || nav <= 0) return toast({ title: "Invalid NAV" });
    if (isNaN(units) || units <= 0) return toast({ title: "Invalid units" });
    setBusy(true);
    try {
      await api.editTransaction(editing.id, {
        date: editing.date,
        type: editing.type,
        details: editing.details,
        funds,
        nav,
        units,
      });
      toast({ title: "Transaction updated" });
      setEditing(null);
      await reload();
    } catch (err) {
      toast({ title: "Failed", description: err.response?.data?.detail || "Could not update" });
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.deleteTransaction(deleting.id);
      toast({ title: "Transaction deleted" });
      setDeleting(null);
      await reload();
    } catch (err) {
      toast({ title: "Failed", description: err.response?.data?.detail || "Could not delete" });
    }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] text-emerald-300/85 uppercase tracking-[0.22em] font-mono">
            <span className="pulse-dot" style={{ width: 6, height: 6 }} />
            Cashflows
          </div>
          <h1 className="text-2xl md:text-4xl font-display text-white mt-1 tracking-tight">Money In / Out</h1>
        </div>
        <ExportButtons
          testIdPrefix="cashflows-export"
          disabled={filtered.length === 0}
          csv={{
            filename: stampedName("cashflows"),
            headers: exportData.headers,
            rows: exportData.rows,
          }}
          pdf={{
            title: "Cashflows",
            subtitle: `${filtered.length} records · Net ${fmtINR(totals.in + totals.out)}`,
            filename: "cashflows",
            orientation: "landscape",
            columns: exportData.pdfColumns,
            rows: exportData.pdfRows,
            summary: [
              ["Inflow", fmtINR(totals.in)],
              ["Outflow", fmtINR(totals.out)],
              ["Net", fmtINR(totals.in + totals.out)],
            ],
          }}
        />
      </div>

      <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <StatCard
          icon={TrendingUp}
          label="Filtered Inflow"
          value={`+${fmtINR(totals.in, { compact: true })}`}
          sub={fmtINR(totals.in)}
          tone="emerald"
          valueClassName="text-emerald-400"
        />
        <StatCard
          icon={TrendingDown}
          label="Filtered Outflow"
          value={fmtINR(totals.out, { compact: true })}
          sub={fmtINR(totals.out)}
          tone="rose"
          valueClassName="text-rose-400"
        />
        <div className="col-span-2 md:col-span-1">
          <StatCard
            icon={Scale}
            label="Net Flow"
            value={fmtINR(totals.in + totals.out, { compact: true })}
            sub={fmtINR(totals.in + totals.out)}
            positive={(totals.in + totals.out) >= 0}
          />
        </div>
      </div>

      {/* Filters row */}
      <div className="mt-5 bg-slate-900 border border-slate-800 rounded-xl p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">Type</div>
            <div className="inline-flex rounded-md border border-slate-800 overflow-hidden">
              {[
                { k: "all", l: "All" },
                { k: "subs", l: "Subscriptions" },
                { k: "reds", l: "Redemptions" },
              ].map((t, i) => (
                <button
                  key={t.k}
                  onClick={() => setFilter(t.k)}
                  data-testid={`cashflow-filter-${t.k}`}
                  className={`px-3 py-1.5 text-xs ${i > 0 ? "border-l border-slate-800" : ""} ${
                    filter === t.k
                      ? "bg-emerald-500 text-slate-950"
                      : "bg-slate-900 text-slate-400 hover:text-white"
                  }`}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>
          <div className="min-w-[180px]">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">Member</div>
            <Select value={memberId} onValueChange={setMemberId}>
              <SelectTrigger data-testid="cashflow-member-filter" className="bg-slate-950 border-slate-800 text-white h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-white max-h-[320px]">
                <SelectItem value="all">All members</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">From</div>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="cashflow-from-date"
              className="bg-slate-950 border-slate-800 text-white h-9 w-[150px]"
            />
          </div>
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">To</div>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="cashflow-to-date"
              className="bg-slate-950 border-slate-800 text-white h-9 w-[150px]"
            />
          </div>
          {filtersActive && (
            <button
              onClick={clearFilters}
              data-testid="cashflow-clear-filters"
              className="text-xs text-slate-400 hover:text-white flex items-center gap-1 h-9 px-3"
            >
              <X size={12} /> Clear
            </button>
          )}
          <div className="ml-auto text-xs text-slate-500">
            {filtered.length} of {transactions.length} records
          </div>
        </div>
      </div>

      <div className="mt-4 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80 text-[11px] text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Member</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-right px-4 py-3">NAV</th>
                <th className="text-right px-4 py-3">Units</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-right px-4 py-3">Running Balance</th>
                {isAdmin && <th className="px-4 py-3"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} className="text-sm text-slate-500 py-12 text-center">
                    No transactions match the filters.
                  </td>
                </tr>
              )}
              {filtered.map((c) => {
                const inflow = (c.fundsCr || 0) > 0;
                const amount = inflow ? c.fundsCr : -c.fundsDr;
                const units = inflow ? c.unitsCr : c.unitsDr;
                return (
                  <tr key={c.id} className="hover:bg-slate-800/50 transition-colors">
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{longDate(c.date)}</td>
                    <td className="px-4 py-3 text-white font-medium truncate max-w-[180px]">{c.name}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${
                        inflow
                          ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                          : "bg-rose-500/10 text-rose-300 border-rose-500/30"
                      }`}>
                        {inflow ? "Subscription" : "Redemption"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-400 font-mono text-xs">₹{c.nav.toFixed(4)}</td>
                    <td className="px-4 py-3 text-right text-slate-400 font-mono text-xs">{fmtUnits(units)}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${inflow ? "text-emerald-400" : "text-rose-400"}`}>
                      {inflow ? "+" : ""}{fmtINR(amount)}
                      {c.amcFee > 0 && (
                        <div className="text-[10px] text-slate-500 font-normal">AMC fee {fmtINR(c.amcFee)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300 font-mono text-xs">
                      {fmtINR(runningById[c.id] || 0)}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end items-center gap-1">
                          <button
                            onClick={() => startEdit(c)}
                            data-testid={`cashflow-edit-${c.id}`}
                            className="text-slate-500 hover:text-emerald-400 p-1.5"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => setDeleting(c)}
                            data-testid={`cashflow-delete-${c.id}`}
                            className="text-slate-500 hover:text-rose-400 p-1.5"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-800">
          {filtered.length === 0 && (
            <div className="text-sm text-slate-500 py-8 text-center">No transactions match filters.</div>
          )}
          {filtered.map((c) => {
            const inflow = (c.fundsCr || 0) > 0;
            const amount = inflow ? c.fundsCr : -c.fundsDr;
            const units = inflow ? c.unitsCr : c.unitsDr;
            return (
              <div key={c.id} className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border ${
                  inflow
                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                    : "bg-rose-500/10 border-rose-500/30 text-rose-400"
                }`}>
                  {inflow ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {longDate(c.date)} · NAV ₹{c.nav.toFixed(4)}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Running: {fmtINR(runningById[c.id] || 0)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-sm font-semibold ${inflow ? "text-emerald-400" : "text-rose-400"}`}>
                    {inflow ? "+" : ""}{fmtINR(amount)}
                  </div>
                  <div className="text-[11px] text-slate-500">{fmtUnits(units)} u</div>
                </div>
                {isAdmin && (
                  <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => startEdit(c)} className="text-slate-500 p-1">
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => setDeleting(c)} className="text-slate-500 p-1">
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Admin edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit transaction</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="text-xs text-slate-400">
                Member: <span className="text-white">{editing.memberName}</span>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Type</Label>
                <div className="mt-2 inline-flex rounded-md border border-slate-800 overflow-hidden">
                  <button
                    onClick={() => updateEdit("type", "subscription")}
                    data-testid="edit-type-subscription"
                    className={`px-4 py-2 text-xs ${editing.type === "subscription" ? "bg-emerald-500 text-slate-950" : "bg-slate-900 text-slate-400"}`}
                  >
                    Subscription
                  </button>
                  <button
                    onClick={() => updateEdit("type", "redemption")}
                    data-testid="edit-type-redemption"
                    className={`px-4 py-2 text-xs border-l border-slate-800 ${editing.type === "redemption" ? "bg-rose-500 text-slate-950" : "bg-slate-900 text-slate-400"}`}
                  >
                    Redemption
                  </button>
                </div>
                <div className="mt-2 text-[11px] text-slate-500">
                  To convert this entry to a Bonus issuance, delete it here and recreate from Admin → New Transaction → Bonus.
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Date</Label>
                  <Input type="date" value={editing.date} onChange={(e) => updateEdit("date", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-800 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">NAV ₹</Label>
                  <Input type="number" step="0.0001" value={editing.nav} onChange={(e) => updateEdit("nav", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-800 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Funds ₹</Label>
                  <Input type="number" step="0.01" value={editing.funds} onChange={(e) => updateEdit("funds", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-800 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Units</Label>
                  <Input type="number" step="0.0001" value={editing.units} onChange={(e) => updateEdit("units", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-800 text-white font-mono" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Details</Label>
                <Textarea value={editing.details} onChange={(e) => updateEdit("details", e.target.value)} rows={2} className="mt-1.5 bg-slate-950 border-slate-800 text-white" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} className="text-slate-400 hover:text-white hover:bg-slate-800">
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="cashflow-save-edit" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete transaction?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This will delete the {deleting?.type} of <span className="text-white">{fmtINR(deleting ? (deleting.fundsCr || deleting.fundsDr) : 0)}</span> for <span className="text-white">{deleting?.name}</span> on <span className="text-white">{deleting && longDate(deleting.date)}</span>. Balances and NAV history will be recomputed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="cashflow-confirm-delete"
              className="bg-rose-500 hover:bg-rose-400 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
